import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { BulkCalendarSyncResult } from '@/hooks/useCalendarSync';
import type { OutlookCalendar } from '@/hooks/useOutlookCalendars';
import type { Task } from '@/components/ui/columns-tasks';
import { Button } from '@/components/ui/NewButton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BulkOutlookSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deadlines: Task[];
  availableCalendars: OutlookCalendar[];
  onSync: (taskIds: string[], calendarId: string) => Promise<BulkCalendarSyncResult>;
}

export function BulkOutlookSyncDialog({
  open,
  onOpenChange,
  deadlines,
  availableCalendars,
  onSync,
}: BulkOutlookSyncDialogProps) {
  const editableCalendars = useMemo(
    () => availableCalendars.filter((calendar) => calendar.canEdit),
    [availableCalendars],
  );
  const preferredCalendarId =
    (editableCalendars.find((calendar) => calendar.isDefaultCalendar) ?? editableCalendars[0])?.id ??
    '';
  const calendarFieldId = useId();
  const futureOnlyFieldId = useId();
  const [selectedCalendarId, setSelectedCalendarId] = useState(preferredCalendarId);
  const [futureOnly, setFutureOnly] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const futureDeadlines = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deadlines.filter((deadline) => deadline.date.getTime() >= today.getTime());
  }, [deadlines]);
  const hasFutureDeadlines = futureDeadlines.length > 0;

  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
      return;
    }
    setSelectedCalendarId(preferredCalendarId);
    setFutureOnly(hasFutureDeadlines);
  }, [hasFutureDeadlines, open, preferredCalendarId]);

  const scopedDeadlines = futureOnly ? futureDeadlines : deadlines;

  const unsyncedDeadlines = useMemo(
    () =>
      selectedCalendarId
        ? scopedDeadlines.filter(
            (deadline) =>
              !(deadline.synced_calendars ?? []).some(
                (sync) => sync.calendar_id === selectedCalendarId,
              ),
          )
        : [],
    [scopedDeadlines, selectedCalendarId],
  );
  const alreadySyncedCount = scopedDeadlines.length - unsyncedDeadlines.length;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isSubmitting && !nextOpen) return;
      onOpenChange(nextOpen);
    },
    [isSubmitting, onOpenChange],
  );
  const handleCancel = useCallback(() => handleOpenChange(false), [handleOpenChange]);
  const handleFutureOnlyChange = useCallback((checked: boolean | 'indeterminate') => {
    setFutureOnly(checked === true);
  }, []);
  const futureOnlyControl = (
    <div className="flex w-fit items-center gap-2">
      <Checkbox
        id={futureOnlyFieldId}
        checked={futureOnly}
        disabled={!hasFutureDeadlines || isSubmitting}
        onCheckedChange={handleFutureOnlyChange}
      />
      <Label
        htmlFor={futureOnlyFieldId}
        className={!hasFutureDeadlines ? 'cursor-not-allowed text-stone-400' : 'cursor-pointer'}
      >
        Sync future deadlines only
      </Label>
    </div>
  );

  const handleSync = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!selectedCalendarId || unsyncedDeadlines.length === 0 || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const result = await onSync(
          unsyncedDeadlines.map((deadline) => deadline.id),
          selectedCalendarId,
        );
        if (result.failed === 0) onOpenChange(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, onOpenChange, onSync, selectedCalendarId, unsyncedDeadlines],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton={isSubmitting}
        onPointerDownOutside={(event) => {
          if (isSubmitting) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (isSubmitting) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src="/microsoft-outlook.svg" alt="" className="h-5 w-5 object-contain" />
            Sync to Outlook
          </DialogTitle>
          <DialogDescription className="pt-2">
            Choose a calendar for all active deadlines in this matter. Future changes will stay in
            sync.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSync} className="space-y-4 md:space-y-6 md:pt-2">
          <div className="space-y-2">
            <Label htmlFor={calendarFieldId} className="md:block md:pb-0.5">
              Outlook calendar
            </Label>
            <Select
              value={selectedCalendarId}
              onValueChange={setSelectedCalendarId}
              disabled={isSubmitting}
            >
              <SelectTrigger id={calendarFieldId}>
                <SelectValue placeholder="Choose a calendar" />
              </SelectTrigger>
              <SelectContent>
                {editableCalendars.map((calendar) => (
                  <SelectItem key={calendar.id} value={calendar.id}>
                    {calendar.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasFutureDeadlines ? (
            futureOnlyControl
          ) : (
            <TooltipProvider delayDuration={250}>
              <Tooltip>
                <TooltipTrigger asChild>{futureOnlyControl}</TooltipTrigger>
                <TooltipContent>No future deadlines found</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <div className="text-sm text-stone-600">
            {unsyncedDeadlines.length > 0 ? (
              <>
                <span className="font-medium text-gray-900">{unsyncedDeadlines.length}</span>{' '}
                deadline{unsyncedDeadlines.length === 1 ? '' : 's'} will be added
                {alreadySyncedCount > 0 && (
                  <span className="text-gray-500">
                    {' '}
                    · {alreadySyncedCount} already synced
                  </span>
                )}
              </>
            ) : (
              futureOnly
                ? 'All future deadlines in this matter are already synced to this calendar.'
                : 'All matter deadlines are already synced to this calendar.'
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              text="Cancel"
              disabled={isSubmitting}
              onClick={handleCancel}
            />
            <Button
              type="submit"
              text="Sync to Outlook"
              loadingText="Syncing to Outlook…"
              loading={isSubmitting}
              disabled={!selectedCalendarId || unsyncedDeadlines.length === 0}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
